import { digObject } from '@domain-group/javascript-utils'

import { useRelations } from '@hooks/useCampaign'

import type { CampaignModel, DataStoreItemModel } from '@models/types'

/*
 * Define the REAXML schema
 * Maps the schema to realhub entity
 *
 * Keep this file in sync with
 * app/integrations/reaxml/services/build_export_values.rb
 */

const portalDataSchema = {
  fields: [
    {
      key: 'airConditioning',
      campaignKey: '',
      label: 'Air Conditioning',
      inputComponent: 'bool',
    },
    {
      key: 'alarmSystem',
      campaignKey: '',
      label: 'Alarm System',
      inputComponent: 'bool',
    },
    {
      key: 'area',
      campaignKey: 'land_area',
      label: 'Land Area',
      inputComponent: 'input',
      inputType: 'number',
    },
    {
      key: 'areaUnit',
      campaignKey: 'land_area_units',
      label: 'Area (Unit)',
      inputComponent: 'select',
      values: [
        { key: 'acre', label: 'Acre' },
        { key: 'hectare', label: 'Hectare' },
        { key: 'square', label: 'Square' },
        { key: 'squareMeter', label: 'Square Meter' },
      ],
    },
    {
      key: 'auctionDate',
      campaignKey: 'auction_date',
      conditions: {
        authority: ['auction'],
      },
      label: 'Auction Date',
      inputComponent: 'input',
      inputType: 'date',
    },
    {
      key: 'auctionTime',
      campaignKey: 'auction_date',
      conditions: {
        authority: ['auction'],
      },
      label: 'Auction Time',
      inputComponent: 'input',
      inputType: 'time',
    },
    {
      key: 'authority',
      campaignKey: '',
      conditions: {
        listingType: ['land', 'residential', 'rental', 'rural'],
      },
      label: 'Authority',
      inputComponent: 'select',
      values: [
        { key: 'auction', label: 'Auction' },
        { key: 'exclusive', label: 'Exclusive' },
        { key: 'multilist', label: 'Multilist' },
        { key: 'conjunctional', label: 'Conjunctional' },
        { key: 'open', label: 'Open' },
        { key: 'sale', label: 'Sale' },
        { key: 'setsale', label: 'Set Sale' },
      ],
    },
    {
      key: 'balcony',
      campaignKey: '',
      label: 'Balcony',
      inputComponent: 'bool',
    },
    {
      key: 'bathrooms',
      campaignKey: 'bathrooms',
      label: 'Bathrooms',
      inputComponent: 'input',
    },
    {
      key: 'bedrooms',
      campaignKey: 'bedrooms',
      label: 'Bedrooms',
      inputComponent: 'input',
    },
    {
      key: 'bond',
      campaignKey: 'rental_bond',
      conditions: {
        listingType: ['rental'],
      },
      label: 'Bond',
      inputComponent: 'input',
      inputType: 'number',
    },
    {
      key: 'broadband',
      campaignKey: '',
      label: 'Broadband',
      inputComponent: 'bool',
    },
    {
      key: 'buildingArea',
      campaignKey: 'building_area',
      label: 'Building Area',
      inputComponent: 'input',
      inputType: 'number',
    },
    {
      key: 'buildingAreaUnit',
      campaignKey: 'building_area_units',
      label: 'Building Area (Unit)',
      inputComponent: 'select',
      values: [
        { key: 'square', label: 'Square' },
        { key: 'squareMeter', label: 'Square Meter' },
      ],
    },
    {
      key: 'builtInRobes',
      campaignKey: '',
      label: 'Built-in Robes',
      inputComponent: 'bool',
    },
    {
      key: 'carports',
      campaignKey: 'carports',
      label: 'Carports',
      inputComponent: 'input',
    },
    {
      key: 'category',
      campaignKey: 'campaign_category_id',
      conditions: {
        listingType: ['residential', 'rental'],
      },
      entityKey: 'campaignCategory.title',
      label: 'Category',
      inputComponent: 'select',
      values: [
        { key: 'AcreageSemi-rural', label: 'Acreage Semi-rural' },
        { key: 'Alpine', label: 'Alpine' },
        { key: 'Apartment', label: 'Apartment' },
        { key: 'BlockOfUnits', label: 'Block Of Units (Only Residential Home)' },
        { key: 'DuplexSemi-detached', label: 'Duplex Semi-detached' },
        { key: 'Flat', label: 'Flat' },
        { key: 'House', label: 'House' },
        { key: 'Retirement', label: 'Retirement' },
        { key: 'ServicedApartment', label: 'Serviced Apartment' },
        { key: 'Studio', label: 'Studio' },
        { key: 'Terrace', label: 'Terrace' },
        { key: 'Townhouse', label: 'Townhouse' },
        { key: 'Unit', label: 'Unit' },
        { key: 'Villa', label: 'Villa' },
        { key: 'Warehouse', label: 'Warehouse' },
        { key: 'Other', label: 'Other' },
      ],
    },
    {
      key: 'commercialAuthority',
      campaignKey: '',
      conditions: {
        listingType: ['commercial'],
      },
      label: 'Commercial Authority',
      inputComponent: 'select',
      values: [
        { key: 'auction', label: 'Auction' },
        { key: 'eoi', label: 'Expression of Interest' },
        { key: 'Forsale', label: 'For Sale' },
        { key: 'offers', label: 'Offers to Purchase' },
        { key: 'Sale', label: 'Sale by Negotiation' },
        { key: 'tender', label: 'Public Tender' },
      ],
    },
    {
      key: 'commercialCategory',
      campaignKey: 'campaign_commercial_category_id',
      conditions: {
        listingType: ['commercial'],
      },
      entityKey: 'campaignCommercialCategory.title',
      label: 'Commercial Category',
      inputComponent: 'select',
      values: [
        { key: 'Commercial Farming', label: 'Commercial Farming' },
        { key: 'Hotel/Leisure', label: 'Hotel/Leisure' },
        { key: 'Industrial/Warehouse', label: 'Industrial/Warehouse' },
        { key: 'Land/Development', label: 'Land/Development' },
        { key: 'Medical/Consulting', label: 'Medical/Consulting' },
        { key: 'Offices', label: 'Offices' },
        { key: 'Retail', label: 'Retail' },
        { key: 'Showrooms/Bulky Goods', label: 'Showrooms/Bulky Goods' },
        { key: 'Other', label: 'Other' },
      ],
    },
    {
      key: 'councilRates',
      campaignKey: 'council_rates',
      label: 'Council Rates',
      inputComponent: 'input',
    },
    {
      key: 'courtyard',
      campaignKey: '',
      label: 'Courtyard',
      inputComponent: 'bool',
    },
    {
      key: 'deck',
      campaignKey: '',
      label: 'Deck',
      inputComponent: 'bool',
    },
    {
      key: 'dishwasher',
      campaignKey: '',
      label: 'Dishwasher',
      inputComponent: 'bool',
    },
    {
      key: 'ductedCooling',
      campaignKey: '',
      label: 'Ducted Cooling',
      inputComponent: 'bool',
    },
    {
      key: 'ductedHeating',
      campaignKey: '',
      label: 'Ducted Heating',
      inputComponent: 'bool',
    },
    {
      key: 'energyRating',
      campaignKey: 'energy_rating',
      label: 'Energy Rating',
      inputComponent: 'input',
    },
    {
      key: 'ensuite',
      campaignKey: '',
      label: 'Ensuites',
      inputComponent: 'input',
    },
    {
      key: 'evaporativeCooling',
      campaignKey: '',
      label: 'Evaporative Cooling',
      inputComponent: 'bool',
    },
    {
      key: 'floorboards',
      campaignKey: '',
      label: 'Floorboards',
      inputComponent: 'bool',
    },
    {
      key: 'frontage',
      campaignKey: 'frontage',
      label: 'Frontage (m)',
      inputComponent: 'input',
      inputType: 'number',
    },
    {
      key: 'fullyFenced',
      campaignKey: '',
      label: 'Fully Fenced',
      inputComponent: 'bool',
    },
    {
      key: 'garages',
      campaignKey: 'carparks',
      label: 'Garages',
      inputComponent: 'input',
    },
    {
      key: 'gasHeating',
      campaignKey: '',
      label: 'Gas Heating',
      inputComponent: 'bool',
    },
    {
      key: 'gym',
      campaignKey: '',
      label: 'Gym',
      inputComponent: 'bool',
    },
    {
      key: 'hideAddress',
      campaignKey: 'hide_address',
      label: 'Hide Address',
      inputComponent: 'bool',
      values: [
        { key: 'true', label: 'Yes' },
        { key: 'false', label: 'No' },
      ],
    },
    {
      key: 'hidePrice',
      campaignKey: 'hide_price',
      label: 'Hide Price',
      inputComponent: 'bool',
      values: [
        { key: 'true', label: 'Yes' },
        { key: 'false', label: 'No' },
      ],
    },
    {
      key: 'hideSoldPrice',
      campaignKey: 'hide_sold_price',
      conditions: {
        status: ['sold'],
      },
      label: 'Hide Sold Price',
      inputComponent: 'bool',
      values: [
        { key: 'true', label: 'Yes' },
        { key: 'false', label: 'No' },
      ],
    },
    {
      key: 'hotWaterService',
      campaignKey: '',
      label: 'Hot Water Service',
      inputComponent: 'bool',
    },
    {
      key: 'hydronicHeating',
      campaignKey: '',
      label: 'Hydronic Heating',
      inputComponent: 'bool',
    },
    {
      key: 'insideSpa',
      campaignKey: '',
      label: 'Spa (Indoor)',
      inputComponent: 'bool',
    },
    {
      key: 'intercom',
      campaignKey: '',
      label: 'Intercom',
      inputComponent: 'bool',
    },
    {
      key: 'landCategory',
      campaignKey: '',
      conditions: {
        listingType: ['land'],
      },
      label: 'Land Category',
      inputComponent: 'select',
      values: [
        { key: 'Commercial', label: 'Commercial' },
        { key: 'Residential', label: 'Residential' },
      ],
    },
    {
      key: 'listingType',
      campaignKey: 'campaign_type_id',
      entityKey: 'campaignType.short_name',
      label: 'Listing Type',
      inputComponent: 'select',
      values: [
        { key: 'commercial', label: 'Commercial Sale & Lease' },
        { key: 'land', label: 'Residential Land' },
        { key: 'rental', label: 'Residential Rental' },
        { key: 'residential', label: 'Residential Home' },
        { key: 'rural', label: 'Rural' },
      ],
    },
    {
      key: 'livingArea',
      campaignKey: 'living_areas',
      label: 'Living Areas',
      inputComponent: 'input',
    },
    {
      key: 'lotNumber',
      campaignKey: 'lot_number',
      label: 'Lot Number',
      inputComponent: 'input',
    },
    {
      key: 'openFirePlace',
      campaignKey: '',
      label: 'Open Fire Place',
      inputComponent: 'bool',
    },
    {
      key: 'openSpaces',
      campaignKey: 'car_spaces',
      label: 'Open Spaces',
      inputComponent: 'input',
    },
    {
      key: 'otherFeatures',
      campaignKey: 'other_features',
      label: 'Other Features',
      inputComponent: 'textarea',
    },
    {
      key: 'outdoorEnt',
      campaignKey: '',
      label: 'Outdoor Entertainment',
      inputComponent: 'bool',
    },
    {
      key: 'outsideSpa',
      campaignKey: '',
      label: 'Spa (Outdoor)',
      inputComponent: 'bool',
    },
    {
      key: 'payTV',
      campaignKey: '',
      label: 'Pay TV',
      inputComponent: 'bool',
    },
    {
      key: 'poolAboveGround',
      campaignKey: '',
      label: 'Pool (Above Ground)',
      inputComponent: 'bool',
    },
    {
      key: 'poolInGround',
      campaignKey: '',
      label: 'Pool (In Ground)',
      inputComponent: 'bool',
    },
    {
      key: 'postcode',
      campaignKey: 'post_code',
      label: 'Postcode',
      inputComponent: 'input',
    },
    {
      key: 'price',
      campaignKey: 'price',
      label: 'Price',
      inputComponent: 'input',
      inputType: 'number',
    },
    {
      key: 'priceView',
      campaignKey: 'price_guide',
      label: 'Price View (50 Chars)',
      inputComponent: 'input',
    },
    {
      key: 'remoteGarage',
      campaignKey: '',
      label: 'Remote Garage',
      inputComponent: 'bool',
    },
    {
      key: 'rentalPeriod',
      campaignKey: 'rental_period',
      conditions: {
        listingType: ['rental'],
      },
      label: 'Period',
      inputComponent: 'select',
      values: [
        { key: 'weekly', label: 'Week' },
        { key: 'monthly', label: 'Monthly' },
      ],
    },
    {
      key: 'reverseCycleAircon',
      campaignKey: '',
      label: 'Aircon (Reverse Cycle)',
      inputComponent: 'bool',
    },
    {
      key: 'rumpusRoom',
      campaignKey: '',
      label: 'Rumpus Room',
      inputComponent: 'bool',
    },
    {
      key: 'ruralCategory',
      campaignKey: 'campaign_rural_category_id',
      conditions: {
        listingType: ['rural'],
      },
      entityKey: 'campaignRuralCategory.title',
      label: 'Rural Category',
      inputComponent: 'select',
      values: [
        { key: 'Cropping', label: 'Cropping' },
        { key: 'Dairy', label: 'Dairy' },
        { key: 'Farmlet', label: 'Farmlet' },
        { key: 'Horticulture', label: 'Horticulture' },
        { key: 'Lifestyle', label: 'Lifestyle' },
        { key: 'Livestock', label: 'Livestock' },
        { key: 'Viticulture', label: 'Viticulture' },
        { key: 'MixedFarming', label: 'Mixed Farming' },
        { key: 'Other', label: 'Other' },
      ],
    },
    {
      key: 'secureParking',
      campaignKey: '',
      label: 'Secure Parking',
      inputComponent: 'bool',
    },
    {
      key: 'setSaleDate',
      campaignKey: '',
      conditions: {
        authority: ['setsale'],
      },
      label: 'Set Sale Date',
      inputComponent: 'input',
      inputType: 'date',
    },
    {
      key: 'setSaleTime',
      campaignKey: '',
      conditions: {
        authority: ['setsale'],
      },
      label: 'Set Sale Time',
      inputComponent: 'input',
      inputType: 'time',
    },
    {
      key: 'shed',
      campaignKey: '',
      label: 'Shed',
      inputComponent: 'bool',
    },
    {
      key: 'soldDate',
      campaignKey: 'sold_date',
      conditions: {
        status: ['sold'],
      },
      label: 'Sold Date',
      inputComponent: 'input',
      inputType: 'date',
    },
    {
      key: 'soldPrice',
      campaignKey: 'sold_price',
      conditions: {
        status: ['sold'],
      },
      label: 'Sold Price',
      inputComponent: 'input',
    },
    {
      key: 'splitsystemAircon',
      campaignKey: '',
      label: 'Aircon (Split System)',
      inputComponent: 'bool',
    },
    {
      key: 'splitsystemHeating',
      campaignKey: '',
      label: 'Heating (Split System)',
      inputComponent: 'bool',
    },
    {
      key: 'state',
      campaignKey: 'state_name',
      label: 'State',
      inputComponent: 'input',
    },
    {
      key: 'status',
      campaignKey: '',
      label: 'Status',
      inputComponent: 'select',
      values: [
        { key: 'current', label: 'Current', listingType: ['commercial', 'land', 'residential', 'rental', 'rural'] },
        { key: 'deleted', label: 'Deleted', listingType: ['commercial', 'land', 'residential', 'rental', 'rural'] },
        { key: 'leased', label: 'Leased', listingType: ['rental'] },
        {
          key: 'offmarket',
          label: 'Off Market',
          listingType: ['commercial', 'land', 'residential', 'rental', 'rural'],
        },
        { key: 'sold', label: 'Sold', listingType: ['commercial', 'land', 'residential', 'rural'] },
        { key: 'withdrawn', label: 'Withdrawn', listingType: ['commercial', 'land', 'residential', 'rental', 'rural'] },
      ],
    },
    {
      key: 'street',
      campaignKey: 'street_name',
      label: 'Street Name',
      inputComponent: 'input',
    },
    {
      key: 'streetNumber',
      campaignKey: 'street_number',
      label: 'Street No.',
      inputComponent: 'input',
    },
    {
      key: 'study',
      campaignKey: 'studies',
      label: 'Study',
      inputComponent: 'bool',
    },
    {
      key: 'suburb',
      campaignKey: 'suburb_name',
      label: 'Suburb',
      inputComponent: 'input',
    },
    {
      key: 'subNumber',
      campaignKey: 'unit_number',
      label: 'Unit No.',
      inputComponent: 'input',
    },
    {
      key: 'tennisCourt',
      campaignKey: '',
      label: 'Tennis Court',
      inputComponent: 'bool',
    },
    {
      key: 'toilet',
      campaignKey: '',
      label: 'Toilets',
      inputComponent: 'input',
    },
    {
      key: 'underOffer',
      campaignKey: 'under_offer',
      label: 'Under Offer',
      inputComponent: 'bool',
    },
    {
      key: 'vacuumSystem',
      campaignKey: '',
      label: 'Vacuum System',
      inputComponent: 'bool',
    },
    {
      key: 'workshop',
      campaignKey: '',
      label: 'Workshop',
      inputComponent: 'bool',
    },
  ],
  requiredFields: {
    commercial: [
      'category',
      'status',
      'commercialCategory',
      'commercialListingType',
      'streetNumber',
      'street',
      'suburb',
      'postcode',
      'country',
      'price',
      'headline',
      'description',
      'area',
    ],
    land: [
      'category',
      'status',
      'landCategory',
      'streetNumber',
      'street',
      'suburb',
      'postcode',
      'country',
      'price',
      'headline',
      'description',
    ],
    rental: [
      'category',
      'status',
      'streetNumber',
      'street',
      'suburb',
      'postcode',
      'country',
      'headline',
      'description',
      'bedrooms',
      'bathrooms',
      'dateAvailable',
      'rent',
    ],
    residential: [
      'category',
      'status',
      'streetNumber',
      'street',
      'suburb',
      'postcode',
      'country',
      'price',
      'headline',
      'description',
      'bedrooms',
      'bathrooms',
    ],
    rural: [
      'category',
      'status',
      'ruralCategory',
      'streetNumber',
      'street',
      'suburb',
      'postcode',
      'country',
      'price',
      'headline',
      'description',
    ],
  },
}

export default portalDataSchema

interface FindDataStoreItemParams {
  dataStoreItems: DataStoreItemModel[],
  key: string,
}

export const findDataStoreItem = (params: FindDataStoreItemParams) => {
  const { dataStoreItems, key } = params
  const item = dataStoreItems.find(i => i.key === key)

  return item || {}
}

export interface FieldSchema {
  key: string,
  campaignKey: string,
  conditions: {
    listingType?: string[],
  },
  entityKey: string,
  label: string,
  inputComponent?: string,
  inputType?: string,
  values: {
    key: string,
    label: string,
  }[],
  valueType?: string,
}

interface FindDefaultValueParams {
  campaign: CampaignModel,
  campaignRelations: object,
  fieldSchema: FieldSchema,
}

export const findDefaultValue = (params: FindDefaultValueParams) => {
  const { campaign, campaignRelations, fieldSchema } = params

  const { campaignKey, entityKey } = fieldSchema

  if (entityKey){
    return digObject(campaignRelations, entityKey, '')
  }

  return digObject(campaign, campaignKey, '')
}

interface ValidateConditionParams {
  conditions: object,
  campaign: CampaignModel,
  campaignRelations: ReturnType<typeof useRelations>,
  dataStoreItems: DataStoreItemModel[],
}

export const validateConditions = (params: ValidateConditionParams) => {
  const {
    conditions, campaign, campaignRelations, dataStoreItems,
  } = params

  if (conditions){
    // Loop through conditions and match them
    const valid = Object.keys(conditions).every((key) => {
      // @ts-expect-error TS7053
      const permittedValues = conditions[key]

      // Check if the value has been overridden by the datastore
      const dataStoreItem = findDataStoreItem({ dataStoreItems, key }) || {}

      // @ts-expect-error TS2339
      if (dataStoreItem.id){
        // @ts-expect-error TS2339
        return permittedValues.includes(dataStoreItem.value)
      }

      // Check if the default value is valid
      const fieldSchema = portalDataSchema.fields.find((field => field.key === key)) || {}

      // @ts-expect-error TS2740
      const defaultValue = findDefaultValue({ campaign, campaignRelations, fieldSchema })

      return permittedValues.includes(defaultValue)
    })

    return valid
  }

  return true
}
